import { Activity, AlertTriangle, ArrowRightLeft, BookOpen, Clapperboard, ClipboardList, Megaphone, DollarSign, Dumbbell, Factory, FileText, Flame, Headset, HeartPulse, Image as ImageIcon, KeyRound, MessageCircle, Mountain, Quote, Route, ScrollText, Server, ShieldAlert, SlidersHorizontal, Smartphone, Target, ToggleRight, Trophy, User, Users } from 'lucide-react'
import type { ComponentType, SVGProps } from 'react'

import type { DashboardUserRole } from '../../lib/api/auth'

export type DashboardRoute = {
  label: string
  path: string
  icon: ComponentType<SVGProps<SVGSVGElement>>
  roles?: DashboardUserRole[]
  keywords?: string[]
}

export type DashboardRouteSearchItem = DashboardRoute & {
  groupId: string
  groupLabel: string
}

export type DashboardRouteGroup = {
  id: string
  label: string
  items: DashboardRoute[]
  defaultExpanded?: boolean
}

export const dashboardRouteGroups: DashboardRouteGroup[] = [
  {
    id: 'users',
    label: 'Users',
    defaultExpanded: true,
    items: [
      { label: 'Dashboard Users', path: '/users/dashboard', icon: Users, roles: ['Admin', 'Editor', 'SupportAdmin'], keywords: ['admins', 'operators', 'staff'] },
      { label: 'Mobile Users', path: '/users/mobile', icon: Smartphone, roles: ['Admin', 'Editor', 'SupportAdmin', 'SupportAgent'], keywords: ['customers', 'members', 'app users'] }
    ]
  },
  {
    id: 'support',
    label: 'Support',
    items: [
      { label: 'Tickets', path: '/support', icon: Headset, roles: ['Admin', 'SupportAdmin', 'SupportAgent'], keywords: ['support', 'customer service', 'inbox'] },
      { label: 'Moderation', path: '/support/moderation', icon: ShieldAlert, roles: ['Admin', 'SupportAdmin', 'SupportAgent'], keywords: ['reports', 'abuse', 'safety'] }
    ]
  },
  {
    id: 'ai-assistant',
    label: 'AI Assistant',
    defaultExpanded: true,
    items: [
      { label: 'Admin Reports', path: '/reports', icon: MessageCircle, roles: ['Admin'], keywords: ['query', 'database', 'chat', 'report', 'users'] },
      { label: 'AI Feedback', path: '/ai-feedback', icon: AlertTriangle, roles: ['Admin', 'Editor', 'SupportAdmin', 'SupportAgent'], keywords: ['assistant', 'llm'] },
      { label: 'System Prompts', path: '/system-prompts', icon: ScrollText, roles: ['Admin', 'Editor'], keywords: ['assistant', 'llm', 'openrouter', 'models'] }
    ]
  },
  {
    id: 'content',
    label: 'Content',
    defaultExpanded: true,
    items: [
      { label: 'Content Promotion', path: '/content-promotion', icon: ArrowRightLeft, roles: ['Admin'], keywords: ['publish', 'sync', 'staging', 'production', 'migration'] },
      { label: 'Popular Hikes', path: '/popular-hikes', icon: Flame, roles: ['Admin', 'Editor'] },
      { label: 'Groups', path: '/social/groups', icon: Users, roles: ['Admin'] },
      { label: 'Fitness Hike Targets', path: '/fitness-hikes', icon: Target, roles: ['Admin', 'Editor'] },
      { label: 'Testimonials', path: '/testimonials', icon: Quote, roles: ['Admin', 'Editor'] },
      { label: 'Exercises', path: '/exercises', icon: Dumbbell, roles: ['Admin', 'Editor'] },
      { label: 'Exercise Videos', path: '/exercise-videos', icon: Clapperboard, roles: ['Admin', 'Editor'] },
      { label: 'Exercise Tracker Instructions', path: '/exercise-tracker-instructions', icon: FileText, roles: ['Admin', 'Editor'] },
      { label: 'Badges', path: '/badges', icon: ImageIcon, roles: ['Admin', 'Editor'], keywords: ['achievements', 'seasonal badge', 'challenge badge'] },
      { label: 'Campaign Messages', path: '/campaign-messages', icon: Megaphone, roles: ['Admin', 'Editor'], keywords: ['announcements', 'notifications', 'push', 'cohorts'] },
      { label: 'Poll Results', path: '/campaign-polls', icon: ClipboardList, roles: ['Admin', 'Editor'], keywords: ['campaigns', 'votes', 'surveys'] },
      { label: 'Challenges', path: '/challenges', icon: Trophy, roles: ['Admin', 'Editor'] },
      { label: 'String Tables', path: '/string-tables', icon: ScrollText, roles: ['Admin'], keywords: ['localization', 'copy', 'translations'] }
    ]
  },
  {
    id: 'tours',
    label: 'Tours',
    items: [
      { label: 'Tours', path: '/tours', icon: Route, roles: ['Admin', 'Editor', 'TourAdmin'] },
      { label: 'Operators', path: '/operators', icon: Factory, roles: ['Admin', 'Editor'] }
    ]
  },
  {
    id: 'training',
    label: 'Training Plans',
    items: [
      { label: 'Plan Tester', path: '/plan-tester', icon: ClipboardList, roles: ['Admin', 'Editor'] },
      { label: 'Plan Algorithm', path: '/plan-algorithm', icon: SlidersHorizontal, roles: ['Admin'], keywords: ['plan config', 'milestone config', 'generation', 'milestones', 'tunables', 'algorithm'] },
      { label: 'HSI Config', path: '/hsi-config', icon: Activity, roles: ['Admin'] }
    ]
  },
  {
    id: 'system',
    label: 'System & Billing',
    items: [
      { label: 'Default Profile Pictures', path: '/default-profile-pictures', icon: ImageIcon, roles: ['Admin', 'Editor'] },
      { label: 'Status', path: '/status', icon: Server, roles: ['Admin', 'Editor'], keywords: ['deployment', 'health', 'service'] },
      { label: 'Application Health', path: '/system/application-health', icon: HeartPulse, roles: ['Admin'], keywords: ['incidents', 'errors', 'failures', 'acknowledgment', 'server'] },
      { label: 'Tracking Alerts', path: '/system/tracking-diagnostics', icon: AlertTriangle, roles: ['Admin'], keywords: ['mobile', 'gps', 'diagnostics', 'location', 'workout'] },
      { label: 'API Keys', path: '/system/api-keys', icon: KeyRound, roles: ['Admin'], keywords: ['cli', 'automation', 'tokens', 'secrets'] },
      { label: 'Feature Flags', path: '/feature-flags', icon: ToggleRight, roles: ['Admin'], keywords: ['rollouts', 'toggles', 'experiments'] },
      { label: 'Revenue', path: '/revenue', icon: DollarSign, roles: ['Admin'], keywords: ['billing', 'subscriptions', 'payments', 'earnings', 'pricing'] }
    ]
  },
  {
    id: 'touradmin',
    label: 'Tour Operator',
    items: [
      { label: 'Tour Operator', path: '/touradmin', icon: Mountain, roles: ['TourAdmin'] }
    ]
  },
  {
    id: 'resources',
    label: 'Resources',
    items: [
      { label: 'Ask', path: '/ask', icon: MessageCircle, roles: ['Admin', 'Editor', 'TourAdmin', 'SupportAdmin', 'SupportAgent'] },
      { label: 'Docs', path: '/docs', icon: BookOpen, roles: ['Admin', 'Editor', 'TourAdmin', 'SupportAdmin', 'SupportAgent'] }
    ]
  },
  {
    id: 'account',
    label: 'Account',
    items: [
      { label: 'Profile', path: '/profile', icon: User }
    ]
  }
]

export const getDashboardRouteGroupsForRole = (role?: DashboardUserRole): DashboardRouteGroup[] => {
  return dashboardRouteGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((route) => {
        if (!route.roles) {
          return true
        }
        return role ? route.roles.includes(role) : false
      })
    }))
    .filter((group) => group.items.length > 0)
}

export const getDashboardRoutesForRole = (role?: DashboardUserRole): DashboardRouteSearchItem[] => {
  return getDashboardRouteGroupsForRole(role).flatMap((group) =>
    group.items.map((route) => ({
      ...route,
      groupId: group.id,
      groupLabel: group.label
    }))
  )
}

/** Keep nested workspaces, such as moderation, distinct from their parent route. */
export const getDashboardRouteContext = (pathname: string, role?: DashboardUserRole) =>
  getDashboardRoutesForRole(role)
    .filter((route) => pathname === route.path || pathname.startsWith(`${route.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0]
